import { BRAND } from "constants/brand";
import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose } from "@fortawesome/free-solid-svg-icons";

import { Form } from "react-bootstrap";

import { CSSTransition } from "react-transition-group";
import { Ring } from "@uiball/loaders";
import useOrder from "hooks/useOrder";
import FieldHelp from "components/Ui/FieldHelp/FieldHelp";

/**
 * "طلب سريع" for a SERVICE vendor — a productless dispatch account (اطلب اي غرض,
 * Talabatk Taxi) logging "go fetch this from somewhere". The dashboard twin of
 * the vendor app's one-field sheet, added so the two surfaces match.
 *
 * Deliberately minimal, and that is the whole design rather than an oversight:
 * a dispatch office does not know the goods value, has no prep time, and has one
 * delivery area configured, so every field the sibling modal shows would be
 * noise here. It mirrors the app sheet exactly.
 *
 * Posts to POST api/order/dispatch with NO discriminator, like every other
 * modal. A vendor flagged `isServiceVendor` resolves to the service policy:
 * subtotal hardcoded 0, born status="finished" so drivers in EVERY city can
 * claim it immediately, the vendor open-check skipped (these accounts are closed
 * most of the day by the working-hours cron), and the area substituted from the
 * vendor's first priced one since this form sends none.
 *
 * NO AREA PICKER, ON PURPOSE. This is why the API cannot require `areaId`
 * globally — see the per-policy `requiredFields` in util/dispatchOrderPolicy.js.
 */
const AddServiceVendorQuickOrder = (props) => {
    const form = useRef();
    const nodeRef = useRef();
    const { loading, createQuickOrder } = useOrder();

    const submitHandler = async (e) => {
        e.preventDefault();

        const raw = new FormData(form.current);
        const shop = String(raw.get("shop") || "").trim();
        const extra = String(raw.get("extra") || "").trim();
        const phone = String(raw.get("phone") || "").trim();

        const orderData = new FormData();
        // Two lines, shop name first — the vendor app composes `notes` the same
        // way (quick_dispatch_sheet.dart), and the driver reads this verbatim.
        // Keeping the composition identical means orders from the dashboard and
        // from the app are indistinguishable to whoever picks them up.
        orderData.append("notes", extra ? `${shop}\n${extra}` : shop);
        if (phone) orderData.append("phone", phone);

        try {
            const response = await createQuickOrder(orderData);

            if (response?.order) {
                props.showMessage("success", "تمت الاضافه", "تمت إضافه الطلب بنجاح");
                props.close();
            } else {
                props.showMessage("error", "هناك خطأ", response?.message || "حدث خطأ غير متوقع");
            }
        } catch (err) {
            props.showMessage(
                "error",
                "هناك خطأ",
                err?.response?.data?.message || "حدث خطأ غير متوقع",
            );
        }
    };

    return (
        <>
            <div className={`model ${props.show ? "model-show" : ""}`} onClick={props.close}>
                <CSSTransition
                    mountOnEnter
                    unmountOnExit
                    nodeRef={nodeRef}
                    in={props.show}
                    timeout={400}
                    classNames="show"
                >
                    <div className="addItem" ref={nodeRef} onClick={(e) => e.stopPropagation()}>
                        {loading ? (
                            <div className="loading-spinner center">
                                <Ring size={40} lineWeight={5} speed={2} color={BRAND} />
                            </div>
                        ) : null}
                        <div className="head center">
                            <h5>طلب سريع</h5>
                            <FontAwesomeIcon icon={faClose} onClick={props.close} />
                        </div>
                        <Form className="form" ref={form} onSubmit={submitHandler}>
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    اسم المحل<span>*</span>
                                    <FieldHelp>
                                        المكان الذي يذهب إليه السائق. نص حر — لا يشترط أن يكون المحل
                                        مسجلًا في التطبيق.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control name="shop" type="text" required />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    تفاصيل الطلب (اختياري)
                                    <FieldHelp>
                                        ما المطلوب إحضاره. يُضاف في سطر جديد تحت اسم المحل، تمامًا
                                        كما في تطبيق المتجر.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control name="extra" as="textarea" rows={3} />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>
                                    رقم الهاتف (اختياري)
                                    <FieldHelp>
                                        اختياري. رقم الزبون الذي سيتصل به السائق عند التوصيل.
                                    </FieldHelp>
                                </Form.Label>
                                <Form.Control name="phone" type="text" />
                            </Form.Group>

                            <div className="buttons">
                                <button className="cancel" onClick={props.close} type="button">
                                    إلغاء
                                </button>
                                <button className="button" type="submit" disabled={loading}>
                                    إضافه
                                </button>
                            </div>
                        </Form>
                    </div>
                </CSSTransition>
            </div>
        </>
    );
};

export default AddServiceVendorQuickOrder;
